const AUTH_LAMBDA_FUNCTION_URL = 'https://jihcfjgfzbcof7fsii3ks4zceq0zibvh.lambda-url.eu-west-2.on.aws/';
const SANDBOX_AUTH_LAMBDA_FUNCTION_URL = 'https://x7wkmxaneqcq7lfb4rbqzvrnji0aclqm.lambda-url.eu-west-2.on.aws/';
const STAFF_MANAGEMENT_LAMBDA_FUNCTION_URLS = Object.freeze([
  'https://xjj63zquwlqvhtndbhzqilyg7i0vwcxq.lambda-url.eu-west-2.on.aws/'
]);
const FALLBACK_AUTH_ERROR_MARKERS = [
  'Invalid key=value pair',
  'Missing Authentication Token'
];

export const STAFF_MANAGEMENT_PRIMARY_URL =
  STAFF_MANAGEMENT_LAMBDA_FUNCTION_URLS[0] || AUTH_LAMBDA_FUNCTION_URL;

const STAFF_PATH_PREFIXES = Object.freeze([
  '/staff/',
  '/api/staff/'
]);

const getAuthTokenOrThrow = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token missing for fallback request.');
  }
  return token;
};

const getTokenIssuer = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let decoded = '';
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      decoded = window.atob(base64);
    } else if (typeof Buffer !== 'undefined') {
      decoded = Buffer.from(base64, 'base64').toString('binary');
    } else {
      return null;
    }
    const jsonPayload = decodeURIComponent(decoded.split('').map((c) => {
      return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`;
    }).join(''));
    const payload = JSON.parse(jsonPayload);
    return payload?.iss || null;
  } catch {
    return null;
  }
};

const isProductionReceevitHost = () => {
  if (typeof window === 'undefined' || !window?.location) {
    return false;
  }
  const hostname = window.location.hostname || '';
  return hostname.includes('receevit.com') && !hostname.includes('sandbox');
};

const shouldUseSandboxAuth = (token) => {
  if (isProductionReceevitHost()) {
    return false;
  }

  try {
    if (typeof window !== 'undefined') {
      const hostname = window.location?.hostname || '';
      if (
        hostname &&
        hostname.includes('receevit.com') &&
        !hostname.includes('sandbox')
      ) {
        return false;
      }
    }
  } catch {
    // ignore window access issues
  }

  try {
    if (typeof localStorage !== 'undefined') {
      const override = localStorage.getItem('__sandboxApiBaseOverride');
      if (override && override.includes('/sandbox')) {
        return true;
      }
    }
  } catch {
    // Ignore localStorage access issues (e.g., server-side rendering)
  }

  const issuer = getTokenIssuer(token);
  return issuer === 'receevit-auth-sandbox';
};

const ensureTrailingSlash = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

const isStaffManagementPath = (path) => {
  if (!path || typeof path !== 'string') {
    return false;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return STAFF_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

const selectFallbackBaseUrl = (token, normalizedPath) => {
  const staffBases = Array.isArray(STAFF_MANAGEMENT_LAMBDA_FUNCTION_URLS)
    ? STAFF_MANAGEMENT_LAMBDA_FUNCTION_URLS.filter(Boolean)
    : [];

  if (isStaffManagementPath(normalizedPath) && staffBases.length > 0) {
    const [primaryStaffBase] = staffBases;
    const ensuredStaffBase = ensureTrailingSlash(primaryStaffBase);
    if (ensuredStaffBase) {
      return ensuredStaffBase;
    }
  }

  const defaultBase = shouldUseSandboxAuth(token)
    ? SANDBOX_AUTH_LAMBDA_FUNCTION_URL
    : AUTH_LAMBDA_FUNCTION_URL;

  return ensureTrailingSlash(defaultBase) || AUTH_LAMBDA_FUNCTION_URL;
};

export const callAuthPostWithFallback = async (
  apiInstance,
  {
    apiPath,
    fallbackPath,
    payload = {},
    method = 'post',
    authMode = 'header',
    requireAuthToken = true,
    fallbackBaseUrlOverride,
    forceFallback = false,
    additionalHeaders = {},
    customQuery = {},
    allowFallbackOnProduction = false,
    disableStaffFallback = false,
    disableFallbackOnAuthErrors = false,
    skipFallback = false,
  }
) => {
  const isProductionHost = isProductionReceevitHost();
  const initialAllowFallback = allowFallbackOnProduction || !isProductionHost;

  const normalizedMethod = String(method || 'post').toLowerCase();
  const requestMethod = normalizedMethod.toUpperCase();
  const useQueryToken = requireAuthToken && authMode === 'query';
  const tokenForQuery = useQueryToken ? getAuthTokenOrThrow() : null;

  const sanitizedFallbackBaseOverride =
    typeof fallbackBaseUrlOverride === 'string' && fallbackBaseUrlOverride.trim()
      ? fallbackBaseUrlOverride.trim()
      : null;
  const usingDirectFallbackBase = Boolean(sanitizedFallbackBaseOverride);

  const targetPathForStaffCheck = fallbackPath || apiPath || '';
  const isStaffPath = isStaffManagementPath(targetPathForStaffCheck);
  const allowFallback =
    initialAllowFallback || (isStaffPath && !disableStaffFallback);

  const executeFallback = async (tokenOverride) => {
    const token = requireAuthToken
      ? tokenOverride || tokenForQuery || getAuthTokenOrThrow()
      : null;

    const fallbackTargetPath = (fallbackPath || apiPath || '').trim();
    const normalizedPath = fallbackTargetPath
      ? fallbackTargetPath.startsWith('/')
        ? fallbackTargetPath
        : `/${fallbackTargetPath}`
      : '';

    const shouldSendBody = !['GET', 'HEAD'].includes(requestMethod);
    const sendAuthInHeader = requireAuthToken && authMode !== 'query';
    const sendAuthInQuery = requireAuthToken && authMode === 'query';

    const fallbackHeaders = {
      Accept: 'application/json',
      ...additionalHeaders,
    };

    if (sendAuthInHeader && token) {
      fallbackHeaders.Authorization = `Bearer ${token}`;
    }

    const baseIsFunctionUrl = usingDirectFallbackBase
      ? sanitizedFallbackBaseOverride?.includes('lambda-url')
      : true;
    const shouldIncludeAuthQuery = Boolean(
      (sendAuthInQuery && token) || (baseIsFunctionUrl && requireAuthToken && token)
    );

    let fallbackUrl;

    if (usingDirectFallbackBase) {
      const trimmedBase = sanitizedFallbackBaseOverride.endsWith('/')
        ? sanitizedFallbackBaseOverride.slice(0, -1)
        : sanitizedFallbackBaseOverride;
      // Ensure apiPath is used if fallbackPath not provided
      const directPath = normalizedPath || (apiPath ? apiPath : '/');
      fallbackUrl = new URL(
        `${trimmedBase}${directPath.startsWith('/') ? '' : '/'}${directPath}`
      );

      if (shouldIncludeAuthQuery && token) {
        fallbackUrl.searchParams.set('authToken', token);
      }

      if (requestMethod === 'GET' && payload && typeof payload === 'object') {
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            fallbackUrl.searchParams.append(key, value);
          }
        });
      }

      if (shouldSendBody) {
        fallbackHeaders['Content-Type'] = 'application/json';
      }
      Object.entries(customQuery || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          fallbackUrl.searchParams.append(key, value);
        }
      });
    } else {
      const baseFallbackUrl = selectFallbackBaseUrl(token, normalizedPath);
      fallbackUrl = new URL(baseFallbackUrl);

      if (normalizedPath) {
        fallbackUrl.searchParams.set('path', normalizedPath);
      }
      if (shouldIncludeAuthQuery && token) {
        fallbackUrl.searchParams.set('authToken', token);
      }

      if (requestMethod === 'GET' && payload && typeof payload === 'object') {
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            fallbackUrl.searchParams.append(key, value);
          }
        });
      }
      Object.entries(customQuery || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          fallbackUrl.searchParams.append(key, value);
        }
      });

      fallbackHeaders['x-http-method'] = requestMethod;
      fallbackHeaders['x-http-path'] = normalizedPath || fallbackPath || apiPath || '/';

      if (shouldSendBody) {
        fallbackHeaders['Content-Type'] = 'text/plain;charset=UTF-8';
      }
    }

    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE !== 'production') {
      console.debug('[authFallback] Using fallback URL:', fallbackUrl.toString());
    }

    const fallbackResponse = await fetch(fallbackUrl.toString(), {
      method: requestMethod,
      headers: fallbackHeaders,
      body: shouldSendBody ? JSON.stringify(payload || {}) : undefined,
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store'
    });

    let fallbackData;
    try {
      fallbackData = await fallbackResponse.json();
    } catch (parseError) {
      throw new Error('Failed to parse fallback response.');
    }

    if (!fallbackResponse.ok || !fallbackData?.success) {
      const fallbackError = fallbackData?.error || fallbackData?.message || 'Fallback request failed.';
      const fallbackErr = new Error(fallbackError);
      fallbackErr.response = {
        status: fallbackResponse.status,
        data: fallbackData,
      };
      throw fallbackErr;
    }

    return fallbackData;
  };

  if (!requireAuthToken && allowFallback && !skipFallback) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE !== 'production') {
      console.debug('[authFallback] requireAuthToken=false – using fallback only for', fallbackPath || apiPath);
    }
    return executeFallback();
  }

  if (isStaffPath && allowFallback && !disableStaffFallback && !skipFallback) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE !== 'production') {
      console.debug('[authFallback] Staff path immediate fallback:', targetPathForStaffCheck);
    }
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE !== 'production') {
      console.debug('[authFallback] Staff path detected – using fallback directly for', targetPathForStaffCheck);
    }
    return executeFallback();
  }

  if (useQueryToken && allowFallback && !skipFallback) {
    console.log('[authFallback] authMode=query – skipping axios, using fallback directly');
    return executeFallback();
  }
  
  if (forceFallback && allowFallback && !skipFallback) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.MODE !== 'production') {
      console.debug('[authFallback] forceFallback=true – bypassing axios for', fallbackPath || apiPath);
    }
    return executeFallback();
  }

  if (!allowFallback && (forceFallback || useQueryToken)) {
    console.debug('[authFallback] Fallback disabled on production host – using axios for', apiPath);
  }

  try {
    let response;
    const axiosConfig = { __skipAuthTokenPurge: true };

    if (useQueryToken) {
      axiosConfig.__skipAuthHeader = true;
    }

    switch (normalizedMethod) {
      case 'get':
        response = await apiInstance.get(apiPath, {
          ...axiosConfig,
          params: {
            ...(payload || {}),
            ...(useQueryToken ? { authToken: tokenForQuery } : {}),
          },
        });
        break;
      case 'put':
      case 'patch':
      case 'post':
        response = await apiInstance[normalizedMethod](apiPath, payload, {
          ...axiosConfig,
          params: {
            ...(axiosConfig.params || {}),
            ...(useQueryToken ? { authToken: tokenForQuery } : {}),
          },
        });
        break;
      case 'delete':
        response = await apiInstance.delete(apiPath, {
          ...axiosConfig,
          params: {
            ...(axiosConfig.params || {}),
            ...(useQueryToken ? { authToken: tokenForQuery } : {}),
          },
          data: payload,
        });
        break;
      default:
        response = await apiInstance.post(apiPath, payload, axiosConfig);
        break;
    }

    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error?.message || '';

    const shouldUseFallback =
      status === 403 &&
      typeof message === 'string' &&
      FALLBACK_AUTH_ERROR_MARKERS.some(marker => message.includes(marker));

    if (!shouldUseFallback || disableFallbackOnAuthErrors) {
      throw error;
    }

    console.log('[authFallback] axios branch triggering fallback for path:', apiPath || fallbackPath);
    return executeFallback();
  }
};

const normalizeStaffPath = (path) => {
  if (!path || typeof path !== 'string') {
    return '/';
  }
  const trimmed = path.trim();
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const buildStaffFallbackUrl = (path, payload, method, customQuery = {}) => {
  const base = ensureTrailingSlash(STAFF_MANAGEMENT_PRIMARY_URL) || AUTH_LAMBDA_FUNCTION_URL;
  const normalizedPath = normalizeStaffPath(path);
  const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const url = new URL(`${trimmedBase}${normalizedPath}`);

  if (method === 'GET' && payload && typeof payload === 'object') {
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });
  }

  Object.entries(customQuery).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value);
    }
  });

  return url;
};

export const callStaffLambdaDirect = async ({
  path,
  method = 'get',
  payload = {},
  additionalHeaders = {},
  customQuery = {}
} = {}) => {
  const requestMethod = String(method || 'get').toUpperCase();
  const token = getAuthTokenOrThrow();
  const url = buildStaffFallbackUrl(path, payload, requestMethod, customQuery);

  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  };

  const shouldSendBody = !['GET', 'HEAD'].includes(requestMethod);
  if (shouldSendBody) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url.toString(), {
    method: requestMethod,
    headers,
    body: shouldSendBody ? JSON.stringify(payload || {}) : undefined,
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
  });

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    const parseError = new Error('Failed to parse staff fallback response.');
    parseError.response = { status: response.status };
    throw parseError;
  }

  if (!response.ok || data?.success === false) {
    const message = data?.error || data?.message || 'Staff fallback request failed.';
    const err = new Error(message);
    err.response = {
      status: response.status,
      data,
    };
    throw err;
  }

  return data;
};

export const __internal = {
  AUTH_LAMBDA_FUNCTION_URL,
  FALLBACK_AUTH_ERROR_MARKERS,
  STAFF_MANAGEMENT_LAMBDA_FUNCTION_URLS,
  STAFF_MANAGEMENT_PRIMARY_URL,
  callStaffLambdaDirect,
};

export { AUTH_LAMBDA_FUNCTION_URL };

